import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Star, Loader2, CheckCircle2, Flag, Ban, RefreshCw, Image as ImageIcon,
  ShieldCheck, User, Mail, Megaphone, Save, Send, Sparkles, X, MailCheck,
  Globe, AlertCircle, ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = '/api';

const STATUS_META = {
  new:      { label: 'New',      cls: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  approved: { label: 'Approved', cls: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  flagged:  { label: 'Flagged',  cls: 'text-yellow-300 border-yellow-400/40 bg-yellow-400/10' },
  rejected: { label: 'Rejected', cls: 'text-red-400 border-red-500/40 bg-red-500/10' },
};

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

// --- module-level subcomponents (stable types; avoid remount/focus loss) ---

function Stars({ n }) {
  return (
    <span className="inline-flex" aria-label={`${n} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={15} strokeWidth={1.5}
          className={i <= n ? 'text-hg-green' : 'text-hg-border'}
          fill={i <= n ? 'currentColor' : 'none'} />
      ))}
    </span>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${m.cls}`}>{m.label}</span>;
}

function ConsentChip({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-sans rounded-sm px-1.5 py-0.5 border ${
      ok ? 'text-hg-green border-hg-green/30 bg-hg-green/10' : 'text-hg-text-dim border-hg-border'
    }`}>
      <ShieldCheck size={10} /> {label}: {ok ? 'yes' : 'no'}
    </span>
  );
}

// The digest's "awaiting reply" definition — keep in sync with
// backend/notifications_api.py (approved + has email + no reply + not dismissed
// + not a born-handled source).
const BORN_HANDLED_SOURCES = ['customer_reply', 'historical_testimonial'];
export function isAwaitingReply(r) {
  return r.status === 'approved' && !!r.reviewer_email && !r.replied_at
    && !r.reply_skipped_at && !BORN_HANDLED_SOURCES.includes(r.source);
}

function ReviewCard({ review, busy, onModerate, onSaveNote, onReplied, onSkipReply }) {
  const [note, setNote] = useState(review.internal_notes || '');
  const c = review.consent || {};
  const isFitterPartner = review.subject_type === 'fitter_partner';
  const spam = (review.abuse || {}).spam_score;

  // --- reply panel (Claude draft → edit → branded send) ---
  const [replyOpen, setReplyOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [replySending, setReplySending] = useState(false);
  const [replyErr, setReplyErr] = useState('');
  const repliedAt = review.replied_at;

  const genDraft = useCallback(async () => {
    setDrafting(true); setReplyErr('');
    try {
      const r = await axios.post(`${API}/reviews/${review.id}/draft-reply`);
      setDraft(r.data.draft || '');
    } catch (e) { setReplyErr(e.response?.data?.detail || 'Could not draft a reply.'); }
    setDrafting(false);
  }, [review.id]);

  const openReply = () => {
    setReplyOpen(true);
    if (!draft && !repliedAt) genDraft();
    else if (repliedAt && !draft) setDraft(review.reply_text || '');
  };

  const sendReply = async () => {
    if (!draft.trim()) { setReplyErr('Reply is empty.'); return; }
    setReplySending(true); setReplyErr('');
    try {
      const r = await axios.post(`${API}/reviews/${review.id}/send-reply`, { reply_text: draft });
      onReplied?.(r.data);
      setReplyOpen(false);
    } catch (e) { setReplyErr(e.response?.data?.detail || 'Send failed.'); }
    setReplySending(false);
  };

  return (
    <div data-testid={`review-card-${review.id}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Stars n={review.rating} />
          <StatusBadge status={review.status} />
          <span className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim">
            {isFitterPartner ? <Megaphone size={11} /> : <User size={11} />}
            {isFitterPartner ? 'Fitter → HG (partner)' : 'Customer → HG'}
          </span>
          {spam != null && spam > 0 && (
            <span className="text-[10px] font-sans text-yellow-400/80" title="spam heuristic score">spam {spam}</span>
          )}
          {isAwaitingReply(review) && (
            <span data-testid={`review-awaiting-${review.id}`}
              className="text-[10px] font-sans uppercase tracking-wider border border-amber-400/50 text-amber-300 rounded-sm px-1.5 py-0.5"
              title="Counted in the digest's 'Reviews awaiting reply' — send a reply or mark 'No reply needed'">
              awaiting reply
            </span>
          )}
          {review.reply_skipped_at && !review.replied_at && (
            <span className="text-[10px] font-sans text-hg-text-dim" title={`Marked by ${review.reply_skipped_by || '—'}`}>
              no reply needed ·{' '}
              <button onClick={() => onSkipReply?.(review.id, false)} className="underline hover:text-hg-text">undo</button>
            </span>
          )}
        </div>
        <span className="text-[11px] font-sans text-hg-text-dim">{fmtDate(review.created_at)}</span>
      </div>

      {review.text && <p className="text-sm text-hg-text/90 font-sans leading-relaxed whitespace-pre-line">{review.text}</p>}

      {review.photo_url && (
        <a href={review.photo_url} target="_blank" rel="noreferrer" className="inline-block">
          <img src={review.photo_url} alt="review attachment"
            className="h-28 w-28 object-cover rounded-sm border border-hg-border hover:border-hg-green" />
        </a>
      )}

      <div className="flex items-center gap-4 flex-wrap text-[11px] font-sans text-hg-text-dim">
        {review.reviewer_name && <span className="inline-flex items-center gap-1"><User size={11} /> {review.reviewer_name}</span>}
        {review.reviewer_email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {review.reviewer_email}</span>}
        {!review.reviewer_name && !review.reviewer_email && <span className="italic">anonymous</span>}
        <span className="text-hg-text-dim">· {review.source || 'direct'}</span>
      </div>

      {/* Consent — so the moderator knows what's allowed publicly */}
      <div className="flex items-center gap-2 flex-wrap">
        <ConsentChip ok={!!c.use_as_testimonial} label="testimonial" />
        <ConsentChip ok={!!c.display_name} label="name" />
        {review.photo_url && <ConsentChip ok={!!c.use_photo} label="photo" />}
      </div>

      {/* Internal notes */}
      <div className="flex items-start gap-2">
        <textarea
          data-testid={`review-note-${review.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={1}
          placeholder="Internal note (not shown to the reviewer)"
          className="flex-1 bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs font-sans text-hg-text focus:outline-none focus:ring-1 focus:ring-hg-green resize-y"
        />
        <button data-testid={`review-savenote-${review.id}`} onClick={() => onSaveNote(review.id, note)} disabled={busy}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green disabled:opacity-40">
          <Save size={12} /> Note
        </button>
      </div>

      {/* Moderation actions */}
      <div className="flex gap-2 flex-wrap border-t border-hg-border pt-3">
        <button data-testid={`review-approve-${review.id}`} onClick={() => onModerate(review.id, 'approved', note)} disabled={busy || review.status === 'approved'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
        </button>
        <button data-testid={`review-flag-${review.id}`} onClick={() => onModerate(review.id, 'flagged', note)} disabled={busy || review.status === 'flagged'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-yellow-400/50 text-yellow-300 rounded-sm hover:bg-yellow-400/10 disabled:opacity-40">
          <Flag size={12} /> Flag
        </button>
        <button data-testid={`review-reject-${review.id}`} onClick={() => onModerate(review.id, 'rejected', note)} disabled={busy || review.status === 'rejected'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-red-400/50 text-red-400 rounded-sm hover:bg-red-400/10 disabled:opacity-40">
          <Ban size={12} /> Reject
        </button>
        {isAwaitingReply(review) && (
          <button
            data-testid={`review-skipreply-${review.id}`}
            onClick={() => onSkipReply?.(review.id, true)} disabled={busy}
            title="Clears this review from the digest's awaiting-reply count without sending anything"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm ml-auto hover:border-hg-green hover:text-hg-green disabled:opacity-40">
            <CheckCircle2 size={12} /> No reply needed
          </button>
        )}
        {review.reviewer_email ? (
          <button
            data-testid={`review-reply-${review.id}`}
            onClick={openReply}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border rounded-sm ${
              isAwaitingReply(review) ? '' : 'ml-auto'
            } ${
              repliedAt ? 'border-hg-green/50 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green'
            }`}
          >
            {repliedAt ? <><MailCheck size={12} /> Replied</> : <><Mail size={12} /> Reply</>}
          </button>
        ) : (
          <span data-testid={`review-noemail-${review.id}`}
            title="No reviewer email on file — can't send a reply"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border/60 text-hg-text-dim/50 rounded-sm ml-auto cursor-not-allowed">
            <Mail size={12} /> No email
          </span>
        )}
      </div>

      {/* In-Hub branded reply (Claude draft → edit → send) */}
      {replyOpen && (
        <div data-testid={`review-replypanel-${review.id}`} className="border-t border-hg-border pt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-sans text-hg-text-dim flex items-center gap-1.5">
              <Sparkles size={12} className="text-hg-green" />
              Branded reply to {review.reviewer_name || review.reviewer_email} — sent from Henry-Griffitts (hgsupport@)
            </span>
            <button onClick={() => setReplyOpen(false)} className="text-hg-text-dim hover:text-hg-text"><X size={14} /></button>
          </div>
          {repliedAt && (
            <div className="text-[11px] font-sans text-hg-green">Already replied {fmtDate(repliedAt)}. Sending again will send another email.</div>
          )}
          <div className="relative">
            <textarea
              data-testid={`review-replytext-${review.id}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={8}
              placeholder={drafting ? 'Drafting with Claude…' : 'Reply text'}
              disabled={drafting}
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm font-sans text-hg-text leading-relaxed focus:outline-none focus:ring-1 focus:ring-hg-green resize-y disabled:opacity-60"
            />
            {drafting && <div className="absolute inset-0 flex items-center justify-center"><Loader2 size={18} className="animate-spin text-hg-green" /></div>}
          </div>
          {replyErr && <div className="text-xs text-red-400 font-sans" data-testid={`review-replyerr-${review.id}`}>{replyErr}</div>}
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid={`review-send-${review.id}`} onClick={sendReply} disabled={replySending || drafting || !draft.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40">
              {replySending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reply
            </button>
            <button data-testid={`review-regen-${review.id}`} onClick={genDraft} disabled={drafting || replySending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green disabled:opacity-40">
              <RefreshCw size={12} /> Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm px-4 py-3 min-w-[110px]">
      <div className="text-[10px] uppercase tracking-wider text-hg-text-dim font-sans">{label}</div>
      <div className={`font-display text-xl mt-0.5 ${accent || 'text-hg-text'}`}>{value}</div>
    </div>
  );
}

// --- Google reviews ------------------------------------------------------------------
//
// Same screen, different data: Google reviews live in their own `g_reviews` collection and
// are never merged with the survey reviews above. Google owns the review itself; the Hub
// owns only the workflow state around it (seen / replied / flagged).
//
// Replying PUBLISHES to Google. That is the single write path this module has, so it is
// two clicks — write, then confirm — and it says out loud what it is about to do.

function GoogleReviewCard({ review, onReplied }) {
  const [draft, setDraft] = useState(review.reply_text || '');
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const replied = !!review.reply_text;

  const post = async () => {
    setSending(true); setErr('');
    try {
      const r = await axios.post(`${API}/google/reviews/${review.id}/reply`, { reply_text: draft });
      onReplied?.(r.data);
      setOpen(false); setConfirming(false);
    } catch (e) { setErr(e.response?.data?.detail || 'Google rejected the reply.'); }
    setSending(false);
  };

  return (
    <div data-testid={`google-review-${review.id}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Stars n={review.star_rating} />
          <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${
            replied ? 'text-hg-green border-hg-green/40 bg-hg-green/10' : 'text-blue-300 border-blue-400/40 bg-blue-400/10'
          }`}>{replied ? 'Replied' : 'New'}</span>
          <span className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim">
            <User size={11} /> {review.reviewer_name || 'Anonymous'}
          </span>
          {review.location_title && (
            <span className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5">
              {review.location_is_australia ? 'AU' : 'US'} · {review.location_title}
            </span>
          )}
        </div>
        <span className="text-[11px] font-sans text-hg-text-dim">{fmtDate(review.review_created_at)}</span>
      </div>

      {review.comment
        ? <p className="text-sm text-hg-text/90 font-sans leading-relaxed whitespace-pre-line">{review.comment}</p>
        : <p className="text-sm text-hg-text-dim font-sans italic">Star rating only — no written review.</p>}

      {replied && (
        <div className="border-l-2 border-hg-green/40 pl-3 space-y-1">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
            Our reply · {review.reply_source === 'hub' ? 'sent from the Hub' : 'replied on Google'} · {fmtDate(review.reply_updated_at)}
          </div>
          <p className="text-[13px] text-hg-text/80 font-sans whitespace-pre-line">{review.reply_text}</p>
        </div>
      )}

      <div className="flex gap-2 flex-wrap border-t border-hg-border pt-3">
        <button data-testid={`google-review-replybtn-${review.id}`} onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green">
          <Send size={12} /> {replied ? 'Edit reply' : 'Reply publicly'}
        </button>
      </div>

      {open && (
        <div className="border-t border-hg-border pt-3 space-y-2">
          <textarea
            data-testid={`google-review-text-${review.id}`}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setConfirming(false); }}
            rows={4}
            placeholder="Your public reply, shown on Google under this review"
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm font-sans text-hg-text leading-relaxed focus:outline-none focus:ring-1 focus:ring-hg-green resize-y"
          />
          {err && <div className="text-xs text-red-400 font-sans" data-testid={`google-review-err-${review.id}`}>{err}</div>}
          {confirming ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 text-[11px] font-sans text-amber-300">
                <AlertCircle size={12} /> This posts publicly on Google, under your business name.
              </span>
              <button data-testid={`google-review-confirm-${review.id}`} onClick={post} disabled={sending || !draft.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40">
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Post it
              </button>
              <button onClick={() => setConfirming(false)} className="text-[11px] font-sans text-hg-text-dim hover:text-hg-text">Cancel</button>
            </div>
          ) : (
            <button data-testid={`google-review-send-${review.id}`} onClick={() => setConfirming(true)} disabled={!draft.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green/50 text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40">
              <Send size={12} /> Post reply to Google
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function GoogleReviewsTab({ surveyStats, surveyAvg }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const p = new URLSearchParams();
      if (status !== 'all') p.set('status', status);
      const r = await axios.get(`${API}/google/reviews?${p.toString()}`);
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load Google reviews.'); }
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  if (err) return <div className="text-xs text-red-400 font-sans" data-testid="google-reviews-error">{err}</div>;

  const s = data?.summary || {};
  const configured = data?.configured?.configured;

  return (
    <div className="space-y-6" data-testid="google-reviews-tab">
      {/* Google tiles beside the survey tiles — two separate bodies of feedback, one home. */}
      <div className="flex gap-3 flex-wrap">
        <StatTile label="Google reviews" value={s.total ?? '—'} />
        <StatTile label="Google avg" value={s.average ?? '—'} accent="text-hg-green" />
        <StatTile label="Unreplied" value={s.unreplied ?? '—'} accent={s.unreplied ? 'text-amber-300' : 'text-hg-green'} />
        <div className="w-px bg-hg-border" />
        <StatTile label="Survey reviews" value={surveyStats?.total ?? '—'} />
        <StatTile label="Survey avg" value={surveyAvg ?? '—'} />
      </div>

      {!configured && (
        <div data-testid="google-reviews-setup" className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-sans text-hg-text">
            <AlertCircle size={14} className="text-amber-300" /> Google reviews aren’t connected yet
          </div>
          <p className="text-[12px] text-hg-text-dim font-sans leading-relaxed">
            Needs the Business Profile API application approved and{' '}
            {(data?.configured?.missing || []).map((m) => (
              <code key={m} className="text-hg-text bg-hg-bg border border-hg-border rounded-sm px-1.5 py-0.5 mr-1">{m}</code>
            ))}
            set. Until then this tab stays empty — reviews on Google are unaffected.
          </p>
        </div>
      )}

      <div className="flex gap-1.5 flex-wrap items-center">
        {['all', 'new', 'replied', 'flagged'].map((st) => (
          <button key={st} data-testid={`google-review-filter-${st}`} onClick={() => setStatus(st)}
            className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors capitalize ${
              status === st ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
            }`}>{st}</button>
        ))}
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green ml-auto">
          <RefreshCw size={12} /> Refresh
        </button>
        {(data?.locations || []).filter((l) => l.review_link).map((l) => (
          <a key={l.gbp_location_id} href={l.review_link} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green">
            {l.title} review link <ExternalLink size={10} />
          </a>
        ))}
      </div>

      {(data?.reviews || []).length === 0 ? (
        <div className="text-center py-12 text-sm text-hg-text-dim font-sans" data-testid="google-reviews-empty">
          {configured ? 'No Google reviews synced yet — run a sync from the Google tab.' : 'Nothing to show until Google is connected.'}
        </div>
      ) : (
        <div className="space-y-3" data-testid="google-reviews-list">
          {data.reviews.map((r) => (
            <GoogleReviewCard key={r.id} review={r}
              onReplied={(updated) => setData((prev) => ({
                ...prev, reviews: prev.reviews.map((x) => (x.id === updated.id ? updated : x)),
              }))} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- main module ---

export default function Reviews() {
  const { user } = useAuth();
  // The dashboard's unreplied-Google-reviews chip deep-links here with ?tab=google.
  const [mainTab, setMainTab] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('tab') === 'google' ? 'google' : 'hub';
    } catch { return 'hub'; }
  });
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  // ?view=awaiting-reply (the digest's deep link) opens straight onto the
  // exact set the digest counted.
  const deepLinkAwaiting = (() => {
    try { return new URLSearchParams(window.location.search).get('view') === 'awaiting-reply'; }
    catch { return false; }
  })();
  const [status, setStatus] = useState(deepLinkAwaiting ? 'approved' : 'new');
  const [awaitingOnly, setAwaitingOnly] = useState(deepLinkAwaiting);
  const [subjectType, setSubjectType] = useState(''); // '' | hg_customer | fitter
  const [rating, setRating] = useState('');
  const [hasPhoto, setHasPhoto] = useState(false);

  const moderator = user?.email || 'admin';

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/reviews/stats`); setStats(r.data); } catch { /* non-critical */ }
  }, []);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const p = new URLSearchParams();
      const effStatus = awaitingOnly ? 'approved' : status;
      if (effStatus !== 'all') p.set('status', effStatus);
      if (subjectType) p.set('subject_type', subjectType);
      if (rating) p.set('rating', rating);
      p.set('limit', '500');
      const r = await axios.get(`${API}/reviews?${p.toString()}`);
      let list = r.data.reviews || [];
      if (awaitingOnly) list = list.filter(isAwaitingReply);
      if (hasPhoto) list = list.filter((x) => x.photo_url);
      setReviews(list);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load reviews.');
    }
    setLoading(false);
  }, [status, awaitingOnly, subjectType, rating, hasPhoto]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const moderate = async (id, newStatus, note) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/reviews/${id}`, { status: newStatus, internal_notes: note, moderated_by: moderator });
      await Promise.all([fetchReviews(), fetchStats()]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Moderation failed.');
    }
    setBusyId(null);
  };

  const saveNote = async (id, note) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/reviews/${id}`, { internal_notes: note, moderated_by: moderator });
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save note.');
    }
    setBusyId(null);
  };

  const skipReply = async (id, skip) => {
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/reviews/${id}/skip-reply`, { skip, actor: moderator });
      setReviews((prev) => (awaitingOnly && skip
        ? prev.filter((x) => x.id !== id)
        : prev.map((x) => (x.id === r.data.id ? r.data : x))));
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update.');
    }
    setBusyId(null);
  };

  const avgRating = (() => {
    if (!stats?.by_rating) return null;
    let sum = 0, n = 0;
    for (let r = 1; r <= 5; r++) { const c = stats.by_rating[String(r)] || 0; sum += r * c; n += c; }
    return n ? (sum / n).toFixed(2) : '—';
  })();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">REVIEWS</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          {mainTab === 'google'
            ? 'Reviews left on the Google Business Profile. Replying here posts publicly on Google.'
            : 'Moderate customer & fitter reviews. Nothing is public until you approve it.'}
        </p>
      </div>

      {/* Two separate bodies of feedback, one home. The collections stay separate. */}
      <div className="flex gap-1.5 flex-wrap border-b border-hg-border pb-3">
        {[['hub', 'Survey & fitter reviews'], ['google', 'Google']].map(([id, label]) => (
          <button key={id} data-testid={`reviews-tab-${id}`} onClick={() => setMainTab(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
              mainTab === id ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
            }`}>
            {id === 'google' && <Globe size={12} />}{label}
          </button>
        ))}
      </div>

      {mainTab === 'google' ? <GoogleReviewsTab surveyStats={stats} surveyAvg={avgRating} /> : (
      <>
      {/* Stats */}
      <div className="flex gap-3 flex-wrap">
        <StatTile label="Total" value={stats?.total ?? '—'} />
        <StatTile label="New" value={stats?.by_status?.new ?? '—'} accent="text-blue-300" />
        <StatTile label="Approved" value={stats?.by_status?.approved ?? '—'} accent="text-hg-green" />
        <StatTile label="Flagged" value={stats?.by_status?.flagged ?? '—'} accent="text-yellow-300" />
        <StatTile label="Rejected" value={stats?.by_status?.rejected ?? '—'} accent="text-red-400" />
        <StatTile label="Avg rating" value={avgRating ?? '—'} />
      </div>

      {/* Filters */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
        <div className="flex gap-1.5 flex-wrap">
          {['new', 'approved', 'flagged', 'rejected', 'all'].map((s) => (
            <button key={s} data-testid={`review-filter-status-${s}`}
              onClick={() => { setAwaitingOnly(false); setStatus(s); }}
              className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
                !awaitingOnly && status === s ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
              }`}>
              {s === 'all' ? 'All' : STATUS_META[s].label}
            </button>
          ))}
          <button data-testid="review-filter-awaiting-reply"
            onClick={() => { setAwaitingOnly(true); setStatus('approved'); }}
            title="Exactly what the digest's 'Reviews awaiting reply' counts"
            className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
              awaitingOnly ? 'border-amber-400 bg-amber-400/10 text-amber-300' : 'border-hg-border text-hg-text-dim hover:border-amber-400/50'
            }`}>
            Awaiting reply
          </button>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <select data-testid="review-filter-subject" value={subjectType} onChange={(e) => setSubjectType(e.target.value)}
            className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:outline-none focus:border-hg-green">
            <option value="">All audiences</option>
            <option value="hg_customer">Customers → HG</option>
            <option value="fitter_partner">Fitters → HG (partner)</option>
          </select>
          <select data-testid="review-filter-rating" value={rating} onChange={(e) => setRating(e.target.value)}
            className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:outline-none focus:border-hg-green">
            <option value="">Any rating</option>
            {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r > 1 ? 's' : ''}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs font-sans text-hg-text-dim cursor-pointer">
            <input type="checkbox" data-testid="review-filter-photo" checked={hasPhoto} onChange={(e) => setHasPhoto(e.target.checked)} className="accent-hg-green" />
            <ImageIcon size={12} /> Has photo
          </label>
          <button onClick={() => { fetchReviews(); fetchStats(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green ml-auto">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-400 font-sans" data-testid="reviews-error">{error}</div>}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 text-sm text-hg-text-dim font-sans" data-testid="reviews-empty">
          No reviews match this filter.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} busy={busyId === r.id} onModerate={moderate} onSaveNote={saveNote}
              onSkipReply={skipReply}
              onReplied={(updated) => setReviews((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))} />
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
